<template>
  <v-container fluid class="lampiran-manager">
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Tutup</v-btn>
      </template>
    </v-snackbar>

    <PageHeader
      icon="mdi-table-cog"
      title="Pengurusan Lampiran"
      subtitle="Sunting nilai jadual lampiran mengikut tahun, jana tahun baharu, dan import / eksport Excel"
    />

    <v-row class="mb-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="py-3 kad__tajuk">
            <v-icon small class="mr-2">mdi-table-cog</v-icon>
            <span class="subtitle-1 font-weight-medium">Pengurus Data Lampiran</span>
          </v-card-title>
          <!-- Bar kawalan — jalur teal yang sama seperti halaman lain, dengan
               label kening di ATAS setiap medan menggantikan label terapung. -->
          <v-card-text class="filter-bar py-3">
            <v-row align="end" dense>
              <v-col cols="12" md="2">
                <div class="fld__label">Tahun</div>
                <v-select
                  v-model="selectedYear"
                  :items="yearOptions"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <div class="fld__label">Jadual</div>
                <v-select
                  v-model="selectedTable"
                  :items="tableOptions"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <div class="fld__label">Data</div>
                <v-select
                  v-model="dataFilter"
                  :items="dataFilterOptions"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="5" class="d-flex align-center justify-end">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="btn-teal text-none mr-2" height="40" v-bind="attrs" v-on="on">
                      <v-icon left small>mdi-calendar-sync</v-icon>Tindakan Tahun
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="promptGenerateYear">
                      <v-list-item-title>Jana Tahun Baharu</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="promptDeleteYear">
                      <v-list-item-title class="red--text">Padam Tahun Semasa</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="btn-teal text-none" height="40" v-bind="attrs" v-on="on">
                      <v-icon left small>mdi-swap-vertical</v-icon>Import / Eksport
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="exportExcel('crosstab')">
                      <v-list-item-title>Eksport Excel Crosstab (Baca Sahaja)</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="exportExcel('flat')">
                      <v-list-item-title>Eksport Excel Flat</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="$refs.importFile.click()">
                      <v-list-item-title>Import Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <input type="file" ref="importFile" class="d-none" accept=".xlsx" @change="handleImportFile" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tapisan + jadual dalam SATU kad. Dahulu ia dua kad berasingan dengan
         jurang di antaranya, jadi tapisan kelihatan seperti kawalan yang tiada
         kaitan dan bukan tapisan BAGI jadual di bawahnya. Digabung, jalur teal
         itu membaca sebagai kepala jadual — corak yang sama seperti Jadual
         Awalan dan Log Audit, di mana bar penapis melekat pada jadualnya. -->
    <v-card v-if="selectedTable" outlined>
      <div v-if="filterDescriptors.length > 0" class="filter-bar pa-3">
        <div class="d-flex align-center mb-2">
          <v-icon small class="mr-2 tapis-ikon">mdi-filter-variant</v-icon>
          <span class="subtitle-2 font-weight-bold">Tapisan Jadual</span>
          <v-spacer></v-spacer>
          <span class="caption grey--text text--darken-2">
            <strong>(W)</strong> = Wajaran <span class="mx-2">|</span> <strong>(TW)</strong> = Tanpa Wajaran
          </span>
        </div>
        <v-row align="center" dense>
          <v-col cols="12" md="3" v-for="desc in visibleFilterDescriptors" :key="desc.column">
            <v-select
              v-model="filters[desc.column]"
              :items="filterOptions[desc.column]"
              item-text="label"
              item-value="value"
              :label="desc.label"
              dense
              outlined
              background-color="white"
              hide-details
              @change="handleFilterSelect(desc.column)"
            ></v-select>
          </v-col>
        </v-row>
      </div>

      <!-- Bar tindakan sunting — di antara tapisan dan jadual, sebelah kerja
           yang dilakukannya. Pada bar kawalan di puncak halaman, pengguna
           terpaksa menatal ke atas semula selepas menyunting baris di bawah.

           Ditunjukkan hanya apabila ada suntingan belum disimpan; bar kosong
           yang kekal di situ hanya menolak jadual ke bawah. -->
      <v-slide-y-transition>
        <div v-if="hasUnsavedChanges" class="sunting-bar">
          <v-icon small class="mr-2 sunting-bar__ikon">mdi-pencil-circle</v-icon>
          <span class="sunting-bar__teks">
            <strong>{{ Object.keys(dirtyRows).length }}</strong>
            baris mempunyai suntingan belum disimpan
          </span>
          <v-spacer></v-spacer>
          <v-btn outlined class="reset-btn text-none mr-2" height="36" @click="cancelChanges">
            <v-icon left small>mdi-undo</v-icon>Batal
          </v-btn>
          <v-btn class="btn-teal text-none" height="36" :loading="saving" @click="saveChanges">
            <v-icon left small>mdi-content-save</v-icon>Simpan
          </v-btn>
        </div>
      </v-slide-y-transition>

      <!-- Suntingan di sini berlaku pada SEL, bukan baris: hanya lajur nilai
           yang boleh diklik, dan lajur dimensi (Negeri, Strata, dsb.) tidak.
           Tiada apa dalam jadual yang membezakan kedua-duanya selain kursor,
           yang hanya kelihatan selepas pengguna sudah menuding di tempat yang
           betul. Nota ini menyatakannya terus. -->
      <div v-if="gridData.length > 0" class="panduan-klik mb-2 d-flex justify-end align-center">
        <v-icon small class="mr-2 panduan-klik__ikon">mdi-cursor-default-click-outline</v-icon>
        <span>Klik mana-mana sel dalam jadual untuk mengemas kini nilainya.</span>
      </div>

      <v-data-table v-if="gridData.length > 0"
        :headers="tableHeaders"
        :items="gridData"
        :loading="loading"
        :server-items-length="totalRows"
        :options.sync="tableOptionsSync"
        :footer-props="{ 'items-per-page-options': [50, 100, 500, 1000] }"
        class="elevation-0 lm-table"
        fixed-header
        height="600"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td v-for="dim in gridDims" :key="'dim-'+dim" class="text-left">
              {{ formatDimValue(item, dim) }}
            </td>
            <td
              v-for="measure in filteredGridMeasures"
              :key="'measure-'+measure"
              class="text-right lampiran-cell"
              @click="openEditDialog(item, measure)"
            >
              <template v-if="isCellDirty(item.id, measure)">
                <span class="red--text text-decoration-line-through mr-1">{{ formatNumber(getOriginalValue(item.id, measure), measure) }}</span>
                <span class="green--text font-weight-bold">{{ formatNumber(item[measure], measure) }}</span>
              </template>
              <template v-else>
                {{ formatNumber(item[measure], measure) }}
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>

      <div v-else-if="!loading" class="pa-10 text-center">
        <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-database-off</v-icon>
        <div class="subtitle-1 grey--text text--darken-2 font-weight-medium">Tiada rekod untuk dipaparkan.</div>
        <div class="caption grey--text">Sila cuba ubah tahun atau tapisan jadual.</div>
      </div>
    </v-card>

    <!-- Pengesahan simpan. Menulis ke jadual lampiran menimpa nilai penerbitan
         sebenar dan tiada fungsi buat asal, jadi pengguna disahkan dahulu —
         berapa banyak baris, dan ke jadual/tahun yang mana. -->
    <v-dialog v-model="saveDialog" max-width="420" persistent>
      <v-card class="text-center pa-6">
        <v-icon size="64" class="berjaya-ikon mb-3">mdi-content-save-alert</v-icon>
        <div class="subtitle-1 font-weight-bold mb-1">Simpan perubahan?</div>
        <div class="berjaya-teks mb-5">
          <strong>{{ Object.keys(dirtyRows).length }}</strong> baris akan dikemas kini
          dalam <strong>{{ selectedTable }}</strong> bagi tahun
          <strong>{{ selectedYear }}</strong>. Nilai asal tidak boleh dipulihkan
          selepas disimpan.
        </div>
        <div class="d-flex" style="gap:8px">
          <v-btn outlined class="reset-btn text-none flex-grow-1" height="40"
            :disabled="saving" @click="saveDialog = false">
            Kembali
          </v-btn>
          <v-btn class="btn-teal text-none flex-grow-1" height="40"
            :loading="saving" @click="doSaveChanges">
            Ya, Simpan
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Pengesahan batal. Menggantikan `window.confirm()`: dialog asli pelayar
         tidak boleh digayakan, kelihatan asing daripada sistem, dan pada
         sesetengah pelayar boleh disekat. Ia juga tidak dapat menunjukkan
         BERAPA BANYAK yang akan hilang — maklumat yang paling penting sebelum
         membuang kerja. -->
    <v-dialog v-model="cancelDialog" max-width="420" persistent>
      <v-card class="text-center pa-6">
        <v-icon size="64" class="amaran-ikon mb-3">mdi-alert-circle</v-icon>
        <div class="subtitle-1 font-weight-bold mb-1">Batalkan semua perubahan?</div>
        <div class="berjaya-teks mb-5">
          <strong>{{ Object.keys(dirtyRows).length }}</strong> baris akan dikembalikan
          kepada nilai asal. Tindakan ini tidak boleh dibuat asal.
        </div>
        <div class="d-flex" style="gap:8px">
          <v-btn outlined class="reset-btn text-none flex-grow-1" height="40" @click="cancelDialog = false">
            Kembali
          </v-btn>
          <v-btn class="btn-danger text-none flex-grow-1" height="40" @click="doCancelChanges">
            Ya, Batalkan
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Popup kejayaan simpan. Dialog, bukan snackbar: snackbar hilang sendiri
         selepas 4 saat dan mudah terlepas pandang — menyimpan data lampiran
         ialah tindakan yang pengguna mahu pengesahan jelas untuknya. `persistent`
         supaya ia tidak tertutup dengan klik luar yang tidak sengaja. -->
    <v-dialog v-model="successDialog" max-width="400" persistent>
      <v-card class="text-center pa-6">
        <v-icon size="64" class="berjaya-ikon mb-3">mdi-check-circle</v-icon>
        <div class="subtitle-1 font-weight-bold mb-1">Berjaya Disimpan</div>
        <div class="berjaya-teks mb-5">{{ successMessage }}</div>
        <v-btn block class="btn-teal text-none" height="40" @click="successDialog = false">
          Tutup
        </v-btn>
      </v-card>
    </v-dialog>

    <!-- Dialogs -->
    <v-dialog v-model="editDialog" max-width="340px">
      <v-card>
        <v-card-title class="subtitle-1">{{ editingMeasure ? humanizeMeasure(editingMeasure) : 'Nilai' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editValue"
            label="Nilai"
            type="number"
            single-line
            autofocus
            @keyup.enter="confirmEdit"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Kembali</v-btn>
          <v-btn class="btn-teal text-none" @click="confirmEdit">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="generateYearDialog" max-width="400px">
      <v-card>
        <v-card-title>Jana Tahun Baharu</v-card-title>
        <v-card-text>
          Sila masukkan tahun baharu untuk dijanakan.
          <v-text-field v-model="newYear" label="Tahun" type="number" outlined dense class="mt-3"></v-text-field>
          <v-alert v-if="generateConflict" type="warning" dense text>
            Tahun {{ newYear }} sudah wujud. Adakah anda pasti mahu set semula (force reseed) tahun ini? Semua data untuk tahun ini akan dipadam.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="generateYearDialog = false">Batal</v-btn>
          <v-btn class="btn-teal text-none" @click="doGenerateYear" :loading="generateLoading">Jana</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteYearDialog" max-width="400px">
      <v-card>
        <v-card-title class="red--text">Padam Tahun</v-card-title>
        <v-card-text>
          Adakah anda pasti mahu memadamkan semua jadual untuk tahun <b>{{ selectedYear }}</b>? Tindakan ini tidak boleh diundurkan.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteYearDialog = false">Batal</v-btn>
          <v-btn color="error" @click="doDeleteYear" :loading="deleteLoading">Padam</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importReportDialog" max-width="800px">
      <v-card>
        <v-card-title>Laporan Import Latihan (Dry Run)</v-card-title>
        <v-card-text>
          <p>Terdapat {{ importReport.totalUpdated }} baris yang akan dikemas kini. Adakah anda pasti untuk meneruskan?</p>
          <v-simple-table dense v-if="importReport.report">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Jadual</th>
                  <th>Jumlah Baris</th>
                  <th>Dikemas kini</th>
                  <th>Tiada Perubahan</th>
                  <th>Dilepaskan (Skipped)</th>
                  <th>Ralat</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stats, tableName) in importReport.report" :key="tableName">
                  <td>{{ tableName }}</td>
                  <td>{{ stats.rows }}</td>
                  <td>{{ stats.updated }}</td>
                  <td>{{ stats.unchanged }}</td>
                  <td>{{ stats.skipped }}</td>
                  <td>{{ stats.errors.length }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="importReportDialog = false">Batal</v-btn>
          <v-btn class="btn-teal text-none" @click="doImportActual" :loading="importLoading">Sahkan Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from '@/plugins/axios';   // configured instance — attaches the auth token

// Reuse a single formatter instead of building one per cell on every render.
const NUMBER_FORMAT = new Intl.NumberFormat('ms-MY', { maximumFractionDigits: 2 });

export default {
  name: 'LampiranManager',
  data() {
    return {
      saveDialog: false,
      cancelDialog: false,
      successDialog: false,
      successMessage: '',
      snackbar: { show: false, message: '', color: 'success' },
      filters: {},
      filterDescriptors: [],
      yearsList: [],
      selectedYear: null,
      selectedTable: null,
      dataFilter: 'Semua',
      dataFilterOptions: [
        { text: 'Semua', value: 'Semua' },
        { text: 'Wajaran', value: 'Wajaran' },
        { text: 'Tanpa Wajaran', value: 'TanpaWajaran' }
      ],
      tableOptions: [
        { header: 'Jadual Utama (Core)' },
        { text: 'Negeri x Daerah', value: 'negxdaerah' },
        { text: 'Negeri x Daerah x Strata', value: 'negxdaerahxstrata' },
        { text: 'Negeri x Daerah x Kaum', value: 'negxdaerahxkaum' },
        { text: 'Negeri x Kaum x Strata', value: 'negxkaumxstrata' },
        { divider: true },
        { header: 'Jadual Komposisi (Perbelanjaan)' },
        { text: 'Negeri x Perbelanjaan', value: 'negxperbelanjaan' },
        { text: 'Negeri x Perbelanjaan x Strata', value: 'negxperbelanjaanxstrata' }
      ],
      
      gridData: [],
      gridDims: [],
      gridMeasures: [],
      dirtyRows: {},
      originalValues: {},
      editValue: null,
      editDialog: false,
      editingItem: null,
      editingMeasure: null,

      loading: false,
      saving: false,
      totalRows: 0,
      tableOptionsSync: { page: 1, itemsPerPage: 100 },

      generateYearDialog: false,
      newYear: '',
      generateConflict: false,
      generateLoading: false,

      deleteYearDialog: false,
      deleteLoading: false,

      importFileObj: null,
      importReportDialog: false,
      importReport: {},
      importLoading: false,
    };
  },
  computed: {
    yearOptions() {
      return this.yearsList.map(y => y.tahun).sort((a, b) => b - a);
    },
    tableHeaders() {
      const headers = [];
      this.gridDims.forEach(dim => {
        let text = dim;
        if (dim === 'NG') text = 'Negeri';
        else if (dim === 'DP') text = 'Daerah';
        else if (dim === 'kod_2d') text = 'Kod Item';
        else text = dim.charAt(0).toUpperCase() + dim.slice(1);
        headers.push({ text, value: dim, sortable: false });
      });
      this.filteredGridMeasures.forEach(m => {
        headers.push({ text: this.humanizeMeasure(m), value: m, sortable: false, align: 'end' });
      });
      return headers;
    },
    filteredGridMeasures() {
      if (this.dataFilter === 'Wajaran') {
        return this.gridMeasures.filter(m => m.startsWith('W_'));
      } else if (this.dataFilter === 'TanpaWajaran') {
        return this.gridMeasures.filter(m => m.startsWith('TW_'));
      }
      return this.gridMeasures;
    },
    hasUnsavedChanges() {
      return Object.keys(this.dirtyRows).length > 0;
    },
    // Descriptors currently visible (hidden dependents excluded).
    visibleFilterDescriptors() {
      return this.filterDescriptors.filter(desc => !this.isFilterHidden(desc));
    },
    // Precompute each select's options once per relevant change so the
    // template hands v-select a stable array reference instead of rebuilding
    // it on every render.
    filterOptions() {
      const map = {};
      this.filterDescriptors.forEach(desc => {
        map[desc.column] = this.getOptionsFor(desc);
      });
      return map;
    }
  },
  watch: {
    tableOptionsSync: {
      handler(newVal, oldVal) {
        if (oldVal.page && (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage)) {
          if (this.hasUnsavedChanges) {
            if(!confirm('Terdapat perubahan yang belum disimpan. Teruskan ke muka surat lain? Perubahan akan hilang.')) {
              // Revert sync object visually
              Object.assign(this.tableOptionsSync, oldVal);
              return;
            }
            this.dirtyRows = {};
            this.originalValues = {};
          }
          this.loadTableData();
        }
      },
      deep: true
    },
    selectedYear(newVal, oldVal) {
      if (oldVal && this.hasUnsavedChanges) {
        if (!confirm('Terdapat perubahan yang belum disimpan. Teruskan?')) {
          this.$nextTick(() => { this.selectedYear = oldVal; });
          return;
        }
        this.dirtyRows = {};
        this.originalValues = {};
      }
      this.gridData = [];
      if (this.selectedTable) { this.tableOptionsSync.page = 1; this.loadTableData(); }
    },
    selectedTable(newVal, oldVal) {
      if (oldVal && this.hasUnsavedChanges) {
        if (!confirm('Terdapat perubahan yang belum disimpan. Teruskan?')) {
          this.$nextTick(() => { this.selectedTable = oldVal; });
          return;
        }
        this.dirtyRows = {};
        this.originalValues = {};
      }
      this.resetFilters();
      this.gridData = [];
      if (newVal) {
        this.fetchFilters();
        if (this.selectedYear) {
          this.tableOptionsSync.page = 1;
          this.loadTableData();
        }
      }
    }
  },
  created() {
    this.fetchYears();
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      if (confirm('Terdapat perubahan yang belum disimpan. Adakah anda pasti mahu meninggalkan halaman ini?')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  methods: {
    resetFilters() {
      this.filterDescriptors.forEach(desc => {
        this.$set(this.filters, desc.column, '__ALL__');
      });
    },
    async fetchFilters() {
      if (!this.selectedTable) return;
      try {
        const url = `${process.env.VUE_APP_API_URL}/lampiran/${this.selectedTable}/filters`;
        const params = this.selectedYear ? { tahun: this.selectedYear } : {};
        const res = await axios.get(url, { params });
        this.filterDescriptors = Array.isArray(res.data) ? res.data : (res.data.filters || []);
        this.resetFilters();
      } catch (e) {
        this.showToast('Ralat mengambil tapisan: ' + e.message, 'error');
      }
    },
    getOptionsFor(desc) {
      let opts = desc.options || [];
      if (desc.dependsOn) {
        const parentVal = this.filters[desc.dependsOn];
        opts = opts.filter(opt => opt[desc.dependsOn] === parentVal);
      }
      return [{ label: 'Semua', value: '__ALL__' }, ...opts];
    },
    isFilterHidden(desc) {
      if (desc.dependsOn) {
        const parentVal = this.filters[desc.dependsOn];
        if (parentVal === '__ALL__' || parentVal === undefined) return true;
      }
      return false;
    },
    onFilterChange(column) {
      this.filterDescriptors.forEach(desc => {
        if (desc.dependsOn === column) {
          this.$set(this.filters, desc.column, '__ALL__');
          this.onFilterChange(desc.column);
        }
      });
    },
    handleFilterSelect(column) {
      this.onFilterChange(column);
      this.loadTable();
    },
    showToast(msg, color = 'success') {
      this.snackbar.message = msg;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    humanizeMeasure(m) {
      let suffix = '';
      if (m.startsWith('W_')) suffix = ' (W)';
      else if (m.startsWith('TW_')) suffix = ' (TW)';
      else if (m.startsWith('JR_')) suffix = ' (JR)';
      else if (m.startsWith('JW_')) suffix = ' (JW)';
      else if (m.startsWith('JTW_')) suffix = ' (JTW)';

      let base = m.replace(/^(W_|TW_|JR_|JW_|JTW_)/, '');
      
      const customLabels = {
        'jmr_dimiliki': 'JMR - Dimiliki',
        'jmr_disewa': 'JMR - Disewa',
        'jmr_kuarters': 'JMR - Kuarters',
        'ba_air_paip_dirawat': 'BA - Air Paip Dirawat',
        'ba_air_paip_awam': 'BA - Air Paip Awam',
        'ba_air_paip_lain': 'BA - Bekalan Air Lain',
        'be_ada': 'BE - Ada',
        'be_tiada': 'BE - Tiada',
        'ks_kediaman': 'KS - Dari Kediaman',
        'ks_kawasan': 'KS - Dari Kawasan Tertentu',
        'ks_tiada': 'KS - Tiada',
      };
      
      if (customLabels[base]) {
         return customLabels[base] + suffix;
      }

      // Default behavior
      let name = m.replace(/_/g, ' ');
      if (m.startsWith('W_')) name = name.substring(2) + ' (W)';
      else if (m.startsWith('TW_')) name = name.substring(3) + ' (TW)';
      else if (m.startsWith('JR_')) name = name.substring(3) + ' (JR)';
      else if (m.startsWith('JW_')) name = name.substring(3) + ' (JW)';
      else if (m.startsWith('JTW_')) name = name.substring(4) + ' (JTW)';
      // Title case
      return name.replace(/\b\w/g, l => l.toUpperCase());
    },
    formatNumber(val, measure) {
      if (val === null || val === undefined || val === '') return '-';
      let numStr = NUMBER_FORMAT.format(Number(val));
      if (measure && (measure.includes('_jmr_') || measure.includes('_ba_') || measure.includes('_be_') || measure.includes('_ks_'))) {
        return numStr + '%';
      }
      return numStr;
    },
    formatDimValue(item, dim) {
      if (dim === 'NG') {
        return item.NG === null ? 'MALAYSIA' : (item.negeri || item.NG);
      }
      if (dim === 'DP') {
        return item.DP === null ? 'Semua Daerah' : (item.daerah || item.DP);
      }
      if (dim === 'kod_2d') {
        return item.kod_2d + (item.kumpulan ? ' - ' + item.kumpulan : '');
      }
      return item[dim] === null ? '-' : item[dim];
    },
    async fetchYears() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/lampiran/years`);
        if (res.data.status === 'OK') {
          this.yearsList = res.data.years;
        }
      } catch (e) {
        this.showToast('Ralat mengambil senarai tahun: ' + e.message, 'error');
      }
    },
    loadTable() {
      if (this.hasUnsavedChanges) {
        if(!confirm('Terdapat perubahan yang belum disimpan. Teruskan?')) return;
        this.dirtyRows = {};
      }
      this.tableOptionsSync.page = 1;
      this.loadTableData();
    },
    async loadTableData() {
      if (!this.selectedYear || !this.selectedTable) return;
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.tableOptionsSync;
        const params = { tahun: this.selectedYear, page, perPage: itemsPerPage };
        
        this.filterDescriptors.forEach(desc => {
          const val = this.filters[desc.column];
          if (val !== '__ALL__' && val !== undefined) {
            params[desc.column] = val === null ? '__NULL__' : val;
          }
        });

        const res = await axios.get(`${process.env.VUE_APP_API_URL}/lampiran/${this.selectedTable}`, {
          params
        });
        if (res.data.status === 'OK') {
          this.gridData = res.data.rows;
          this.totalRows = res.data.total;
          this.gridDims = res.data.dims;
          // Filter out precalculate columns
          this.gridMeasures = res.data.measures.filter(m => {
            return !m.startsWith('JR_') && !m.startsWith('JW_') && !m.startsWith('JTW_');
          });
          
          // Ensure all measure fields are reactive
          this.gridData.forEach(row => {
            this.gridMeasures.forEach(m => {
              if (row[m] === undefined) {
                this.$set(row, m, null);
              }
            });
          });

          this.dirtyRows = {};
          this.originalValues = {};
        }
      } catch (e) {
        this.showToast('Ralat memuat jadual: ' + e.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    openEditDialog(item, measure) {
      this.editingItem = item;
      this.editingMeasure = measure;
      this.onCellOpen(item, measure);
      this.editDialog = true;
    },
    confirmEdit() {
      if (this.editingItem && this.editingMeasure) {
        this.onCellSave(this.editingItem, this.editingMeasure);
      }
      this.editDialog = false;
    },
    onCellOpen(item, measure) {
      this.editValue = item[measure];
      
      // Capture original value BEFORE any edits
      if (!this.originalValues[item.id]) {
        this.$set(this.originalValues, item.id, {});
      }
      if (this.originalValues[item.id][measure] === undefined) {
        this.$set(this.originalValues[item.id], measure, item[measure]);
      }
    },
    onCellSave(item, measure) {
      let val = this.editValue;
      if (val === '' || val === null) val = null;
      else val = Number(val);

      if (item[measure] !== val) {
        if (!this.dirtyRows[item.id]) {
          this.$set(this.dirtyRows, item.id, { id: item.id });
        }
        
        const origVal = this.originalValues[item.id][measure];
        
        if (origVal === val) {
          this.$delete(this.dirtyRows[item.id], measure);
          this.$delete(this.originalValues[item.id], measure);
          if (Object.keys(this.dirtyRows[item.id]).length === 1) {
            this.$delete(this.dirtyRows, item.id);
            this.$delete(this.originalValues, item.id);
          }
        } else {
          this.$set(this.dirtyRows[item.id], measure, val);
        }
        
        item[measure] = val;
      }
    },
    // Hanya membuka pengesahan — pemulihan sebenar dilakukan oleh
    // doCancelChanges() selepas pengguna mengesahkan.
    cancelChanges() {
      if (!Object.keys(this.dirtyRows).length) return;
      this.cancelDialog = true;
    },
    doCancelChanges() {
      Object.keys(this.dirtyRows).forEach(id => {
        const item = this.gridData.find(r => String(r.id) === String(id));
        if (item) {
          Object.keys(this.originalValues[id]).forEach(measure => {
            item[measure] = this.originalValues[id][measure];
          });
        }
      });
      this.dirtyRows = {};
      this.originalValues = {};
      this.cancelDialog = false;
    },
    isCellDirty(id, measure) {
      return this.dirtyRows[id] && this.dirtyRows[id][measure] !== undefined;
    },
    getOriginalValue(id, measure) {
      if (this.originalValues[id] && this.originalValues[id][measure] !== undefined) {
        return this.originalValues[id][measure];
      }
      return null;
    },
    // Hanya membuka pengesahan — PUT sebenar dilakukan oleh doSaveChanges().
    saveChanges() {
      if (!Object.keys(this.dirtyRows).length) return;
      this.saveDialog = true;
    },
    async doSaveChanges() {
      const rows = Object.values(this.dirtyRows);
      if (!rows.length) return;
      this.saving = true;
      try {
        const res = await axios.put(`${process.env.VUE_APP_API_URL}/lampiran/${this.selectedTable}`, { rows });
        if (res.data.status === 'OK') {
          // Dialog, bukan toast: lihat nota pada <v-dialog v-model="successDialog">.
          // Ralat kekal sebagai toast — pengguna tidak perlu mengesahkan kegagalan
          // sebelum boleh mencuba semula.
          this.saveDialog = false;
          this.successMessage = `${res.data.updated} rekod telah dikemas kini.`;
          this.successDialog = true;
          this.dirtyRows = {};
          this.originalValues = {};
          this.loadTableData(); // Reload for consistency
        } else {
          this.showToast('Ralat semasa kemas kini.', 'error');
        }
      } catch (e) {
        this.showToast('Ralat menyimpan: ' + (e.response?.data?.message || e.message), 'error');
      } finally {
        this.saving = false;
        // Ditutup juga pada kegagalan: toast ralat berada DI BELAKANG dialog
        // `persistent`, jadi jika dibiarkan terbuka pengguna nampak butang
        // berhenti berpusing tanpa sebarang penjelasan.
        this.saveDialog = false;
      }
    },
    promptGenerateYear() {
      this.newYear = '';
      this.generateConflict = false;
      this.generateYearDialog = true;
    },
    async doGenerateYear() {
      if (!this.newYear) return;
      this.generateLoading = true;
      try {
        const payload = { tahun: parseInt(this.newYear), force: this.generateConflict };
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/lampiran/years`, payload);
        if (res.status === 201 || res.data.status === 'OK') {
          this.showToast(`Tahun ${res.data.tahun} berjaya dijana.`);
          this.generateYearDialog = false;
          await this.fetchYears();
          this.selectedYear = parseInt(this.newYear);
        }
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.generateConflict = true;
        } else {
          this.showToast('Ralat menjana tahun: ' + (e.response?.data?.message || e.message), 'error');
        }
      } finally {
        this.generateLoading = false;
      }
    },
    promptDeleteYear() {
      if (!this.selectedYear) {
        this.showToast('Sila pilih tahun dahulu', 'warning');
        return;
      }
      this.deleteYearDialog = true;
    },
    async doDeleteYear() {
      this.deleteLoading = true;
      try {
        const res = await axios.delete(`${process.env.VUE_APP_API_URL}/lampiran/years/${this.selectedYear}?confirm=true`);
        if (res.data.status === 'OK') {
          this.showToast(`Tahun ${this.selectedYear} berjaya dipadam.`);
          this.deleteYearDialog = false;
          this.selectedYear = null;
          this.gridData = [];
          await this.fetchYears();
        }
      } catch (e) {
        this.showToast('Ralat memadam tahun: ' + (e.response?.data?.message || e.message), 'error');
      } finally {
        this.deleteLoading = false;
      }
    },
    exportExcel(format) {
      if (!this.selectedYear) {
        this.showToast('Sila pilih tahun untuk dieksport', 'warning');
        return;
      }
      const url = `${process.env.VUE_APP_API_URL}/lampiran/export?format=${format}&tahun=${this.selectedYear}`;
      window.location.href = url;
    },
    async handleImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.importFileObj = file;
      this.importLoading = true;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/lampiran/import?dry=true`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = res.data;
        
        if (data.status === 'NO_MATCH') {
          this.showToast(data.message, 'error');
          return;
        }
        
        if (data.status === 'OK') {
          this.importReport = data;
          this.importReportDialog = true;
          
          const changed = data.totalUpdated;
          const unchanged = Object.values(data.report).reduce((s, r) => s + r.unchanged, 0);
          const errors = Object.values(data.report).reduce((s, r) => s + r.errors.length, 0);
          
          this.showToast(`[Semakan] ${changed} baris akan diubah, ${unchanged} tiada perubahan` + (errors ? `, ${errors} ralat` : ''), 'info');
        }
      } catch (e) {
        this.showToast('Ralat dry-run import: ' + (e.response?.data?.message || e.message), 'error');
      } finally {
        this.importLoading = false;
        event.target.value = ''; // Reset file input
      }
    },
    async doImportActual() {
      if (!this.importFileObj) return;
      this.importLoading = true;
      const formData = new FormData();
      formData.append('file', this.importFileObj);
      
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/lampiran/import?dry=false`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = res.data;
        
        if (data.status === 'NO_MATCH') {
          this.showToast(data.message, 'error');
          return;
        }
        
        if (data.status === 'OK') {
          const changed = data.totalUpdated;
          const unchanged = Object.values(data.report).reduce((s, r) => s + r.unchanged, 0);
          const errors = Object.values(data.report).reduce((s, r) => s + r.errors.length, 0);
          
          this.showToast(`${changed} baris dikemaskini, ${unchanged} tiada perubahan` + (errors ? `, ${errors} ralat` : ''));
          
          this.importReportDialog = false;
          this.importFileObj = null;
          if (this.selectedTable) this.loadTableData();
        }
      } catch (e) {
        this.showToast('Ralat import: ' + (e.response?.data?.message || e.message), 'error');
      } finally {
        this.importLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Kepala kad — jalur putih dengan ikon teal, corak yang sama seperti halaman
   lain. Dahulu `<v-card-title>` teks kosong tanpa ikon atau garis pemisah. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon,
.tapis-ikon {
  color: #037769 !important;
}

/* ---------- Jalur penapis / kawalan ---------- */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar >>> .v-input fieldset { border-color: #cfeae7; }
.filter-bar >>> .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar >>> .v-select__selection--comma { color: #21403c; }
.filter-bar >>> .v-input__append-inner .v-icon { color: #7aa8a2; }

/* Butang teal — menggantikan success / warning / info / secondary Vuetify, yang
   memberi empat warna berbeza kepada empat kawalan setara pada satu baris. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
/* `.v-icon` Vuetify tidak mewarisi warna butang — tanpa ini ikon menjadi
   kelabu gelap atas teal dan hampir hilang. */
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}
.reset-btn.v-btn--outlined .v-icon { color: #037769; }

.reset-btn.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .26) !important; }

/* Bar tindakan sunting. Kuning amber dan bukan teal: ia menandakan keadaan
   BELUM SELESAI (ada perubahan yang belum disimpan), bukan sebahagian daripada
   rangka kerja navigasi — pengguna sepatutnya perasan ia berbeza. */
.sunting-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  background: #fff8e1;
  /* Jalur penuh lebar dalam kad, bukan kotak berasingan — hanya birai bawah,
     supaya ia bersambung dengan tapisan di atas dan jadual di bawah. */
  border-top: 1px solid #ffe082;
  border-bottom: 1px solid #ffe082;
}
.sunting-bar__ikon { color: #b98900 !important; }
.berjaya-ikon { color: #037769 !important; }
.amaran-ikon { color: #d9534f !important; }
/* Merah, bukan teal: ini tindakan MEMBUANG kerja. Butang yang memusnahkan
   tidak sepatutnya memakai warna yang sama seperti butang Simpan. */
.btn-danger.v-btn {
  background: #d9534f !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-danger.v-btn:hover { background: #b3312d !important; }
.btn-danger.v-btn .v-icon { color: #ffffff !important; }
.berjaya-teks {
  font-size: var(--fs-sm);
  color: #4a8a84;
}
.sunting-bar__teks {
  font-size: var(--fs-sm);
  color: #6b5200;
}

/* Kepala jadual teal, sepadan dengan jadual Jadual Awalan. */
.lm-table >>> thead th {
  background: #e6f6f5 !important;
  color: #01463e !important;
  font-weight: 700;
  border-bottom: 2px solid #cfeae7 !important;
}
.lm-table >>> tbody tr:hover { background: #eef8f6 !important; }

/* Nota panduan di atas jadual. */
.panduan-klik {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  background: #f7fcfb;
  border-bottom: 1px solid #dbeeeb;
  font-size: var(--fs-sm);
  line-height: 1.5;
  color: #4a8a84;
}
.panduan-klik__ikon {
  color: #037769 !important;
  margin-top: 2px;
}
/* Contoh sebaris yang mencerminkan gaya sel kotor sebenar dalam jadual, supaya
   pengguna mengenali paparan itu apabila melihatnya. */
.panduan-klik__lama {
  color: #d9534f;
  text-decoration: line-through;
  margin: 0 4px 0 2px;
}
.panduan-klik__baru {
  color: #2e7d32;
  font-weight: 700;
}

.lampiran-cell {
  cursor: pointer;
}
.lampiran-cell:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
